import { Change, changeOf } from "../../shared/chartMath";

/** How settling changed a case's worst outcome, as the risk-reward chart plots it. */
export interface WorstOutcomeChange {
  // settled worst − litigated worst (+ = the worst outcome got better)
  exposureRemoved: number;
  // the settlement closed the case: a single, certain result is left
  settledCertain: boolean;
  // probability of the worst litigated outcome; absent on snapshots stored
  // before it was recorded
  litigatedWorstProbability?: number;
}

export type WorstOutcomeMessageKey =
  | "trialBetter"
  | "trialWorse"
  | "trialSame"
  | "worstBetter"
  | "worstWorse"
  | "worstSame";

// how each reading of the change ends a message key
const DIRECTION = {
  better: "Better",
  unchanged: "Same",
  worse: "Worse",
} as const satisfies { [change in Change]: string };

/**
 * Which sentence describes the change and with which figures. A case the
 * settlement closed has one result left, compared with the worst trial outcome
 * and how likely that was. A case with outcomes still open has a new worst
 * outcome, which can only be compared by value: its probability belongs to a
 * different set of outcomes than the old worst's.
 */
export function worstOutcomeMessage({
  exposureRemoved,
  settledCertain,
  litigatedWorstProbability,
}: WorstOutcomeChange): {
  key: WorstOutcomeMessageKey;
  amount: number;
  probability?: number;
} {
  const direction = DIRECTION[changeOf(exposureRemoved)];
  return {
    key: `${settledCertain ? "trial" : "worst"}${direction}`,
    amount: Math.abs(exposureRemoved),
    probability: settledCertain ? litigatedWorstProbability : undefined,
  };
}
